import React, { useState } from "react"
import { Checkbox, Loading, Modal } from "@wfp/ui"
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons"
import Button from "components/Shared/Button/Button"
import { GorpReportData } from "interfaces/dataInterfaces"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { useNavigate, useParams } from "react-router-dom"
import { useInsertDataMutation, useValidateReportMutation } from "services/gorp"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import EditData from "./EditData"

interface ErrorData {
  key: string
  errors: string[]
}

interface ReportData {
  errorList: ErrorData[]
  warningList: ErrorData[]
  gorpValues: GorpReportData[]
}

const errorDataSort = (a: ErrorData, b: ErrorData) => {
  if (a.key === "generic") return -1
  if (b.key === "generic") return 1
  return a.key.localeCompare(b.key)
}

interface ErrorListProps {
  errorList: ErrorData[]
}

function ErrorList({ errorList }: ErrorListProps) {
  // errorList.sort(errorDataSort)
  return (
    <div className="max-h-96 overflow-auto">
      <h3 className="text-xl font-bold">Errors</h3>
      {errorList.length === 0 ? (
        <p key="0">No error found in the workBookData.</p>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        errorList.map((error, index) =>
          error.errors.map((errorMessage, errorMessageIndex) => {
            if (error.key === "generic") {
              return <p key={`${error.key}-${index}-${errorMessageIndex}`}>{errorMessage}</p>
            }
            return (
              <p key={`${error.key}-${index}-${errorMessageIndex}`}>
                Row {error.key}: {errorMessage}
              </p>
            )
          }),
        )
      )}
    </div>
  )
}

interface WarningListProps {
  warningList: ErrorData[]
}

function WarningList({ warningList }: WarningListProps) {
  const sortedList = [...warningList].sort(errorDataSort)
  return (
    <div className="max-h-96 overflow-auto">
      <h3 className="text-xl font-bold mt-1">Warnings</h3>
      {sortedList.length === 0 ? (
        <p key="0">No warnings found in the workBookData.</p>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        sortedList.map((warning, index) =>
          warning.errors.map((warningMessage, warningMessageIndex) => {
            if (warning.key === "generic") {
              return <p key={`${warning.key}-${index}-${warningMessageIndex}`}>{warningMessage}</p>
            }
            return (
              <p key={`${warning.key}-${index}-${warningMessageIndex}`}>
                Row {warning.key}: {warningMessage}
              </p>
            )
          }),
        )
      )}
    </div>
  )
}

interface Props {
  reportData: ReportData
}

const STATUS_MAPPING = {
  1: { class: "blue", text: "Validate" },
  2: { class: "bg-red-600", text: "Validated With Errors" },
  3: { class: "bg-green-600", text: "Validated" },
}

export default function ViewReport({ reportData: workBookData }: Props) {
  const { reportId } = useParams()
  const navigate = useNavigate()

  const [validateReport, { isLoading }] = useValidateReportMutation()
  const [saveReport, { isLoading: isSaving }] = useInsertDataMutation()

  const [reportData, setReportData] = useState<ReportData>({ ...workBookData })
  const [showModal, setShowModal] = useState<boolean>(!!workBookData?.errorList || !!workBookData?.warningList)
  const [validationStatus, setValidationStatus] = useState<1 | 2 | 3>(1) // 1 => toBeValidated, 2 => Failed, 3 => Passed
  const [failedErrors, setFailedErrors] = useState<{ errorList: ErrorData[]; warningList: ErrorData[] }>({
    errorList: [],
    warningList: [],
  })
  const [deleteRow, setDeleteRow] = useState<number | null>(null)
  const [editRow, setEditRow] = useState<number | null>(null)

  const handleDeleteRow = () => {
    if (deleteRow === null || !reportData?.gorpValues[deleteRow]) return
    const gorpValues = [...(reportData?.gorpValues || {})]
    gorpValues.splice(deleteRow, 1)
    setReportData({ ...reportData, gorpValues }) // as splice changes the original array so we've to re-render the workBookData
    setDeleteRow(null)
  }

  const handleEditRow = (workBookDataToEdit: any) => {
    if (editRow === null || !reportData?.gorpValues[editRow]) return
    const gorpValues = [...(reportData?.gorpValues || {})]
    gorpValues[editRow] = workBookDataToEdit
    setReportData({ ...reportData, gorpValues }) // we've to re-render the workBookData
    setEditRow(null)
  }

  const handleValidate = async () => {
    if (!reportData?.gorpValues?.length) {
      alert("No workBookData available for validation!")
      return
    }

    const response = await validateReport(reportData?.gorpValues).unwrap()
    setValidationStatus(response?.errorList?.length ? 2 : 3)
    setReportData({
      gorpValues: response?.gorpValues,
      errorList: response?.errorList,
      warningList: response?.warningList,
    })
    setFailedErrors({ errorList: response?.errorList, warningList: response?.warningList })
  }

  const handleSave = async () => {
    if (!reportId || !reportData?.gorpValues.length) return
    saveReport({ values: reportData.gorpValues, rid: +reportId }).then(() => navigate("/Gorp/GorpReports"))
  }

  const parseGorpValue = (value?: number | null, inMain: boolean = false) => {
    if (inMain === true) {
      return "In Main"
    }
    if (value === undefined || value === null) {
      return ""
    }
    return value
  }

  const parsePrevalence = (prevalence?: number | null, inMain: boolean = false) => {
    if (inMain === true) {
      return ""
    }
    if (prevalence === undefined || prevalence === null) {
      return ""
    }
    return prevalence
  }

  const centeredHeaderRenderer = ({ column }) => (
    <div className="BaseTable__header-cell-text text-center">{column.title}</div>
  )

  const getRowClassName = ({ rowData }) => {
    console.log("failedErrors", failedErrors.errorList)
    console.log("reportData", reportData.errorList)
    if (rowData.id === undefined || rowData.id === null) return ""
    const id = rowData.id.toString()
    if (reportData?.errorList?.findIndex(error => error.key === id) !== -1) {
      return "error"
    }
    if (reportData?.warningList?.findIndex(warning => warning.key === id) !== -1) {
      return "warning"
    }
    return ""
  }

  const columns: Array<ColumnShape<GorpReportData>> = [
    {
      key: "id",
      dataKey: "id",
      title: "Row",
      width: 40,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpDateYear",
      dataKey: "gorpDateYear",
      title: "Year",
      width: 55,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpDateMonth",
      dataKey: "gorpDateMonth",
      title: "Month",
      width: 65,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpVisibilityId",
      dataKey: "gorpVisibilityId",
      title: "Visibility",
      width: 80,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "countryOfficeIso3",
      dataKey: "countryOfficeIso3",
      title: "Country",
      width: 70,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpTotalValue",
      dataKey: "gorpTotalValue",
      title: "Total Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpTotalValue),
    },
    {
      key: "gorpTotalPrevalence",
      dataKey: "gorpTotalPrevalence",
      title: "Total Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpTotalPrevalence),
    },
    {
      key: "gorpMainValue",
      dataKey: "gorpMainValue",
      title: "Main Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpMainValue),
    },
    {
      key: "gorpMainPrevalence",
      dataKey: "gorpMainPrevalence",
      title: "Main Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpMainPrevalence),
    },
    {
      key: "gorpAdditionalRefugeesValue",
      dataKey: "gorpAdditionalRefugeesValue",
      title: "Refugees Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpAdditionalRefugeesValue, rowData.areRefugeesInMain),
    },
    {
      key: "gorpAdditionalRefugeesPrevalence",
      dataKey: "gorpAdditionalRefugeesPrevalence",
      title: "Refugees Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpAdditionalRefugeesPrevalence, rowData.areRefugeesInMain),
    },
    {
      key: "gorpAdditionalIdpvalue",
      dataKey: "gorpAdditionalIdpvalue",
      title: "IDP Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpAdditionalIdpvalue, rowData.areIdpinMain),
    },
    {
      key: "gorpAdditionalIdpprevalence",
      dataKey: "gorpAdditionalIdpprevalence",
      title: "IDP Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpAdditionalIdpprevalence, rowData.areIdpinMain),
    },
    {
      key: "gorpIsBaseline",
      title: "Base",
      width: 50,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
      cellRenderer: ({ rowData }) => (
        <Checkbox
          checked={rowData.gorpIsBaseline}
          id="gorpCheckbox"
          labelText=""
          name="checkbox-1"
          value="standard"
          readOnly
        />
      ),
    },
    {
      key: "action",
      title: "Action",
      width: 220,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
      cellRenderer: ({ rowIndex }) => (
        <>
          <Button action={() => setEditRow(rowIndex)} label="Edit" className="btn-full blue action-btn" icon={faPen} />

          <Button
            action={() => setDeleteRow(rowIndex)}
            label="Delete"
            className="btn-full danger action-btn"
            style={{ marginLeft: "10px" }}
            icon={faTrash}
          />
        </>
      ),
    },
  ]

  return (
    <div className="mt-1">
      <Loading active={isLoading || isSaving} />
      <div style={{ height: "100%", minHeight: "60vh" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              fixed
              data={reportData?.gorpValues || []}
              width={width}
              height={height}
              rowKey="key"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
              rowClassName={getRowClassName}
            />
          )}
        </AutoResizer>
      </div>
      <Modal
        open={showModal}
        modalHeading="Validation Report"
        secondaryButtonText="Close"
        passiveModal
        onRequestClose={() => setShowModal(false)}
      >
        <ErrorList errorList={reportData.errorList} />
        <WarningList warningList={reportData.warningList} />
      </Modal>
      <Modal
        open={failedErrors.errorList.length > 0 || failedErrors.warningList.length > 0}
        modalHeading="Validation errors"
        secondaryButtonText="Close"
        passiveModal
        onRequestClose={() => setFailedErrors({ errorList: [], warningList: [] })}
      >
        <ErrorList errorList={failedErrors.errorList} />
        <WarningList warningList={failedErrors.warningList} />
      </Modal>

      {editRow !== null && (
        <EditData
          onSubmit={handleEditRow}
          handleClose={() => setEditRow(null)}
          actionItem={reportData?.gorpValues[editRow as number]}
        />
      )}

      <DeleteConfirmationModal
        open={deleteRow !== null}
        onRequestClose={() => setDeleteRow(null)}
        onRequestSubmit={handleDeleteRow}
      >
        Are you sure you want to delete the selected record? If you decide to remove this record from here it will not
        be imported in the database.
      </DeleteConfirmationModal>
      <div className="mt-2 d-flex">
        <Button
          className={`text-white ${STATUS_MAPPING[validationStatus].class}`}
          action={handleValidate}
          label={STATUS_MAPPING[validationStatus].text}
        />

        {validationStatus === 3 && (
          <Button className="text-white bg-green-600" action={handleSave} label="Insert Data & Save Report" />
        )}
      </div>
    </div>
  )
}
